import { gsap } from 'gsap';

const STORAGE_KEY = 'durham_coins_calculator_cart';

// Off for now by request - flip to true to have the cart survive a page reload via localStorage.
const PERSIST_CART = false;

document.addEventListener('DOMContentLoaded', () => {
    const form = document.querySelector('#calculator-form');
    if (!form) {
        return;
    }

    const weightInput = document.querySelector('#weight');
    const unitSelect = document.querySelector('#unit');
    const tableBody = document.querySelector('#calculator-table-body');
    const totalValueEl = document.querySelector('#calculator-total-value');
    const errorEl = document.querySelector('#calculator-form-error');
    const resetButton = document.querySelector('#calculator-reset');
    const metalTypeGroup = document.querySelector('[data-field="metal-type"]');
    const itemGroups = document.querySelectorAll('.calculator_item_group');
    const submitButton = document.querySelector('#calculator-submit');

    const ADD_LABEL = 'Add to Quote';
    const UPDATE_LABEL = 'Update Quote';
    let isEditing = false;

    // Inputs only, never a computed price/total - prices are always re-fetched fresh.
    let cart = loadCart();

    // Parallel to `cart`; lets a removal resum from an already-fetched figure, no round trip.
    let lastFetchedLines = [];

    function loadCart() {
        if (!PERSIST_CART) {
            return [];
        }

        try {
            const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            return [];
        }
    }

    function saveCart() {
        if (!PERSIST_CART) {
            return;
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    }

    function showError(message) {
        errorEl.textContent = message || '';
    }

    function makeRowId() {
        return (window.crypto && window.crypto.randomUUID)
            ? window.crypto.randomUUID()
            : 'row-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    }

    function formatGBP(value) {
        const decimals = value < 100 ? 2 : 0;
        return '£' + Number(value).toLocaleString('en-GB', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    }

    const UNIT_LABELS = { grams: 'g', kilograms: 'kg', troy_ounces: 'Ozt' };

    function formatWeight(weight, unit) {
        const label = UNIT_LABELS[unit] || unit;
        return unit === 'troy_ounces' ? `${weight} ${label}` : `${weight}${label}`;
    }

    const ICON_PATHS = {
        edit: ['M12 20h9', 'M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z'],
        remove: ['M18 6 6 18', 'M6 6l12 12'],
    };

    function makeIconButton(action) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `calculator_icon_btn calculator_icon_btn--${action}`;
        button.setAttribute('aria-label', action === 'edit' ? 'Edit item' : 'Remove item');

        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('fill', 'none');
        svg.setAttribute('stroke', 'currentColor');
        svg.setAttribute('stroke-width', '2');
        svg.setAttribute('stroke-linecap', 'round');
        svg.setAttribute('stroke-linejoin', 'round');

        ICON_PATHS[action].forEach((d) => {
            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', d);
            svg.appendChild(path);
        });

        button.appendChild(svg);
        return button;
    }

    function renderRow(line) {
        const tr = document.createElement('tr');
        tr.dataset.rowId = line.rowId;

        const nameCell = document.createElement('td');

        const itemName = document.createElement('span');
        itemName.className = 'calculator_cell_name';
        itemName.textContent = line.itemName;

        const weightLabel = document.createElement('span');
        weightLabel.className = 'calculator_cell_weight';
        weightLabel.textContent = `(${formatWeight(line.weight, line.unit)})`;

        nameCell.appendChild(itemName);
        nameCell.appendChild(document.createElement('br'));
        nameCell.appendChild(weightLabel);

        const amount = document.createElement('span');
        amount.className = 'calculator_cell_amount';
        amount.textContent = formatGBP(line.lineTotal);

        const actions = document.createElement('span');
        actions.className = 'calculator_cell_actions';
        actions.appendChild(makeIconButton('edit'));
        actions.appendChild(makeIconButton('remove'));

        const cell = document.createElement('div');
        cell.className = 'calculator_cell';
        cell.appendChild(amount);
        cell.appendChild(actions);

        const valueCell = document.createElement('td');
        valueCell.appendChild(cell);

        tr.appendChild(nameCell);
        tr.appendChild(valueCell);

        return tr;
    }

    // Odometer-style roll from whatever's on screen to the new total.
    const totalProxy = { value: 0 };

    function animateTotalTo(newTotal) {
        gsap.killTweensOf(totalProxy);
        gsap.to(totalProxy, {
            value: newTotal,
            duration: 0.6,
            ease: 'power1.out',
            onUpdate: () => {
                totalValueEl.textContent = formatGBP(totalProxy.value);
            },
        });
    }

    function renderTable(lines, grandTotal) {
        tableBody.innerHTML = '';
        lines.forEach((line) => tableBody.appendChild(renderRow(line)));
        animateTotalTo(grandTotal);
    }

    function recomputeTotalFromMemory() {
        const total = lastFetchedLines.reduce((sum, line) => sum + line.lineTotal, 0);
        animateTotalTo(total);
    }

    function animateNewRow(rowId) {
        const row = tableBody.querySelector(`tr[data-row-id="${rowId}"]`);
        if (row) {
            gsap.from(row, { opacity: 0, y: -12, duration: 0.35, ease: 'power2.out' });
        }
    }

    function animateRowOut(row, onComplete) {
        gsap.to(row, { opacity: 0, x: 12, duration: 0.2, ease: 'power1.in', onComplete });
    }

    function setSubmitLoading(isLoading) {
        submitButton.disabled = isLoading;
        gsap.killTweensOf(submitButton);

        if (isLoading) {
            submitButton.textContent = 'Adding…';
            gsap.to(submitButton, { opacity: 0.5, duration: 0.15 });
        } else {
            submitButton.textContent = isEditing ? UPDATE_LABEL : ADD_LABEL;
            gsap.to(submitButton, { opacity: 1, duration: 0.15 });
        }
    }

    async function fetchQuote(lines) {
        const response = await fetch('/calculator/quote', {
            method: 'POST',
            headers: {
                'X-CSRF-TOKEN': window.csrfToken(),
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({
                lines: lines.map((line) => ({
                    item_id: line.itemId,
                    weight: line.weight,
                    unit: line.unit,
                })),
            }),
        });

        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.message || 'Something went wrong pricing your quote.');
        }

        return response.json();
    }

    function applyQuoteResponse(quotedCart, data) {
        lastFetchedLines = quotedCart.map((line, index) => ({
            rowId: line.rowId,
            itemName: data.lines[index].item_name,
            lineTotal: data.lines[index].line_total,
            weight: line.weight,
            unit: line.unit,
        }));

        renderTable(lastFetchedLines, data.grand_total);
    }

    async function loadAndRenderCart() {
        if (cart.length === 0) {
            lastFetchedLines = [];
            renderTable([], 0);
            return;
        }

        try {
            const data = await fetchQuote(cart);
            applyQuoteResponse(cart, data);
        } catch (error) {
            showError(error.message);
        }
    }

    // Pill selection, extended to show only the item group matching the selected metal type.
    document.querySelectorAll('.form_field_radio').forEach((group) => {
        group.addEventListener('click', (e) => {
            const clicked = e.target.closest('.pill');
            if (!clicked) {
                return;
            }

            group.querySelectorAll(':scope .pill').forEach((pill) => pill.classList.remove('selected'));
            clicked.classList.add('selected');

            if (group === metalTypeGroup) {
                const selectedCategory = clicked.dataset.value;
                itemGroups.forEach((itemGroup) => {
                    itemGroup.classList.toggle('hidden', itemGroup.dataset.category !== selectedCategory);
                });
            }
        });
    });

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        showError('');

        const visibleItemGroup = Array.from(itemGroups).find((group) => !group.classList.contains('hidden'));
        const selectedItemPill = visibleItemGroup ? visibleItemGroup.querySelector('.pill.selected') : null;
        const weight = parseFloat(weightInput.value);

        if (!selectedItemPill) {
            showError('Please select an item.');
            return;
        }

        if (!weightInput.value || Number.isNaN(weight) || weight <= 0) {
            showError('Please enter a valid weight.');
            return;
        }

        const nextCart = [
            ...cart,
            {
                rowId: makeRowId(),
                itemId: parseInt(selectedItemPill.dataset.value, 10),
                weight,
                unit: unitSelect.value,
            },
        ];

        setSubmitLoading(true);

        try {
            const data = await fetchQuote(nextCart);
            const newRowId = nextCart[nextCart.length - 1].rowId;
            cart = nextCart;
            saveCart();
            applyQuoteResponse(cart, data);
            animateNewRow(newRowId);
            weightInput.value = '';
            isEditing = false;
        } catch (error) {
            showError(error.message);
        } finally {
            setSubmitLoading(false);
        }
    });

    tableBody.addEventListener('click', (e) => {
        const row = e.target.closest('tr[data-row-id]');
        if (!row) {
            return;
        }

        const rowId = row.dataset.rowId;
        const index = cart.findIndex((line) => line.rowId === rowId);
        if (index === -1) {
            return;
        }

        if (e.target.closest('.calculator_icon_btn--remove')) {
            cart.splice(index, 1);
            lastFetchedLines.splice(index, 1);
            saveCart();
            animateRowOut(row, () => {
                row.remove();
                recomputeTotalFromMemory();
            });
            return;
        }

        if (e.target.closest('.calculator_icon_btn--edit')) {
            const line = cart[index];
            const targetItemPill = document.querySelector(`.calculator_item_group .pill[data-value="${line.itemId}"]`);
            const targetItemGroup = targetItemPill ? targetItemPill.closest('.calculator_item_group') : null;

            if (targetItemGroup) {
                const categoryKey = targetItemGroup.dataset.category;

                metalTypeGroup.querySelectorAll('.pill').forEach((pill) => {
                    pill.classList.toggle('selected', pill.dataset.value === categoryKey);
                });

                itemGroups.forEach((group) => group.classList.toggle('hidden', group !== targetItemGroup));

                targetItemGroup.querySelectorAll('.pill').forEach((pill) => {
                    pill.classList.toggle('selected', pill === targetItemPill);
                });
            }

            weightInput.value = line.weight;
            unitSelect.value = line.unit;

            isEditing = true;
            submitButton.textContent = UPDATE_LABEL;

            cart.splice(index, 1);
            lastFetchedLines.splice(index, 1);
            saveCart();
            animateRowOut(row, () => {
                row.remove();
                recomputeTotalFromMemory();
            });

            weightInput.focus();
        }
    });

    resetButton.addEventListener('click', () => {
        if (cart.length === 0) {
            return;
        }

        if (!window.confirm('Clear your whole quote?')) {
            return;
        }

        cart = [];
        lastFetchedLines = [];
        saveCart();
        renderTable([], 0);
        showError('');

        isEditing = false;
        submitButton.textContent = ADD_LABEL;
    });

    loadAndRenderCart();
});
